var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import ConsentModel from '../model/consent';
import consentFactory from '../model/consent-factory';
import { canUseCookies } from '../util/storage';
import API from './api';
const requiresConsentModel = (target, propertyKey, propertyDescriptor) => {
    const method = propertyDescriptor.value;
    propertyDescriptor.value = function (parameter, callback) {
        if (!this.consentModel) {
            return callback(null, false);
        }
        method.call(this, parameter, callback);
    };
};
export default class BackendAPI extends API {
    constructor() {
        super(...arguments);
        this.isReady = false;
        this.isSingleFrame = false;
    }
    static async load(backendBaseUrl, clientConsentString, geoInfo, legislation, pVendorList) {
        const api = new this(backendBaseUrl);
        await api.init(clientConsentString, geoInfo, legislation, pVendorList);
        return api;
    }
    async init(clientConsentString, geoInfo, legislation, pVendorList) {
        API.provision();
        this.consentModel = await consentFactory(this.backendBaseUrl, ConsentModel, clientConsentString, geoInfo, legislation, pVendorList);
        this.isReady = true;
        this.emit('ready');
    }
    hasStorageAccess(_, callback) {
        const hasStorageAccess = canUseCookies();
        callback(hasStorageAccess, true);
    }
    getVendorConsents(vendorIds, callback) {
        const ids = vendorIds && vendorIds.length
            ? vendorIds
            : this.consentModel.getAllVendorIds();
        callback({
            gdprApplies: true,
            hasGlobalScope: false,
            metadata: this.consentModel.daisybit,
            purposeConsents: this.consentModel.vendorList.purposes.reduce((consentObject, purpose) => ({
                ...consentObject,
                [purpose.id]: this.consentModel.consented,
            }), {}),
            vendorConsents: ids.reduce((consentObject, id) => ({
                ...consentObject,
                [id]: this.consentModel.consented,
            }), {}),
        }, true);
    }
    getConsentData(consentStringVersion, callback) {
        if (typeof consentStringVersion !== 'undefined' &&
            consentStringVersion !== null &&
            consentStringVersion !== this.consentModel.getConsentVersion().toString()) {
            callback(null, true);
        }
        callback({
            consentData: this.consentModel.daisybit,
            gdprApplies: true,
            hasGlobalScope: false,
        }, true);
    }
    getUSPData(_, callback) {
        callback({
            uspString: this.consentModel.USPString,
            version: 1,
        }, true);
    }
    ping(_, callback) {
        callback({
            cmpLoaded: !!this.consentModel,
            gdprAppliesGlobally: false,
        }, true);
    }
    /* MOL API */
    async consentToAll(origin, callback = () => { }) {
        this.consentModel.consented = true;
        try {
            await this.consentModel.save(origin);
            callback(null, true);
        }
        catch (error) {
            callback(null, false);
        }
    }
    async dissentToAll(origin, callback = () => { }) {
        this.consentModel.consented = false;
        try {
            await this.consentModel.save(origin);
            callback(null, true);
        }
        catch (error) {
            callback(null, false);
        }
    }
    async setCCPANotApplicableStatus([origin, channel], callback = () => { }) {
        try {
            await this.consentModel.setCCPANotApplicableStatus([origin, channel]);
            callback(null, true);
        }
        catch (error) {
            callback(null, false);
        }
    }
    onConsent(_, callback) {
        this.consentModel.on('update', () => {
            if (this.consentModel.consented) {
                callback(null, true);
            }
        });
    }
    onDissent(_, callback) {
        this.consentModel.on('update', () => {
            if (!this.consentModel.consented) {
                callback(null, true);
            }
        });
    }
    onUpdate(_, callback) {
        this.consentModel.on('update', () => {
            callback(this.consentModel.consented, true);
        });
    }
    hasChoiceBeenMade(_, callback) {
        callback(this.consentModel.hasChoiceBeenMade(), true);
    }
    hasConsentedToAll(_, callback) {
        callback(this.consentModel.consented, true);
    }
    ready(_, callback) {
        if (this.isReady) {
            callback(null, true);
        }
        else {
            this.on('ready', () => callback(null, true));
        }
    }
    reset(_, callback) {
        this.consentModel.destroy();
        callback(null, true);
    }
    onReset(_, callback) {
        this.consentModel.on('destroy', () => callback(null, true));
    }
    getVendorList(_, callback) {
        callback(this.consentModel.vendorList, true);
    }
    getMetadata(_, callback) {
        callback({ isRunningInSingleFrame: this.isSingleFrame }, true);
    }
    async updateCookieIfNeeded(_, callback = () => { }) {
        try {
            await this.consentModel.upgradeConsent();
            callback(null, true);
        }
        catch (error) {
            callback(null, false);
        }
    }
    call(command, parameter = null, callback = () => { }) {
        // @ts-ignore
        if (typeof this[command] !== 'function') {
            throw new Error(`"${command}" is not a function`);
        }
        // @ts-ignore
        this[command](parameter, callback);
    }
}
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "getVendorConsents", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "getConsentData", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "getUSPData", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "consentToAll", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "dissentToAll", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "setCCPANotApplicableStatus", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "onConsent", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "onDissent", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "onUpdate", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "hasChoiceBeenMade", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "hasConsentedToAll", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "ready", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "reset", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "onReset", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "getVendorList", null);
__decorate([
    requiresConsentModel
], BackendAPI.prototype, "updateCookieIfNeeded", null);
